import { Lock, CheckCircle2, Eye, FileText, Send, AlertTriangle } from "lucide-react";
import { Link } from "wouter";
import {
  PageHeading, Section, RoleBadge, pathFor, Callout,
  Diagram, MockField, MockButton,
} from "../shared";

export default function SignOffSection() {
  return (
    <div data-testid="guide-sign-off">
      <PageHeading
        icon={Lock}
        title="Sign-off, Review & Closing Rules"
        description="The system has a few 'gates' that stop an item from moving on without the right approvals. These are decided when the item is registered and then enforced for the rest of its life."
      />

      <Callout variant="info" title="In plain words">
        A gate is just a rule that says "you can't go further until X is done". The system has four of them: someone must <span className="text-white">sign off</span> before closing, certain items must go through <span className="text-white">review</span> first, closing always needs a written <span className="text-white">note</span>, and sending an item upward always needs a <span className="text-white">dispatch note</span>.
      </Callout>

      <Section title="When do I use this?">
        <p className="text-sm text-white/65 leading-relaxed">
          You'll meet these gates whenever you try to close or move an item along. This page explains why a button might be greyed out and what you need to do to unlock it.
        </p>
      </Section>

      <Section title="The closing gate, step by step">
        <Diagram
          title="Why the Close button is locked until sign-off"
          frameLabel="Action Item detail → Close"
          legend={[
            <>The <span className="text-white">Sign-off</span> area is where the designated authority approves. Until they do, closing is blocked.</>,
            <>Once sign-off is granted, you must type a <span className="text-white">closing note</span> explaining why the item is done.</>,
            <>The green <span className="text-white">Close Task</span> button only lights up after sign-off (when required) and once the note has a few characters.</>,
          ]}
        >
          <div className="rounded-md border border-white/10 bg-white/[0.03] p-3">
            <div className="flex items-center gap-2 text-xs">
              <span className="inline-flex w-4 h-4 rounded-full bg-cyan-500/30 text-cyan-100 text-[10px] font-bold items-center justify-center ring-1 ring-cyan-400/50">1</span>
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-300" />
              <span className="text-white/70">Sign-off recorded by authority</span>
              <span className="text-emerald-300 ml-auto">Granted</span>
            </div>
          </div>
          <MockField label="Closing note" placeholder="e.g. Board resolution passed unanimously on 15 Mar" marker={2} />
          <MockButton label="Close Task" marker={3} />
        </Diagram>
      </Section>

      <Section title="The four gates">
        <div className="space-y-3">
          <div className="p-3 rounded-md bg-white/[0.02]" data-testid="text-rule-signoff">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle2 className="w-4 h-4 text-green-400" />
              <p className="text-sm text-white font-medium">Sign-off Requirement</p>
            </div>
            <p className="text-xs text-white/55 leading-relaxed mb-2">
              When "Sign-off Required" is switched on, the item <span className="text-white font-medium">cannot be closed</span> until a sign-off is recorded. The sign-off area appears on the detail page for the designated authority to approve. Once granted, the green Close Task button appears.
            </p>
            <p className="text-xs text-white/55 leading-relaxed">
              <span className="text-white font-medium">From v2.5.0:</span> if you Approve &amp; Close from the MD Meetings queue, the system records the sign-off automatically when it's needed — there's no separate Sign-off &amp; Close button anymore. See the <Link href={pathFor("forms/approve-close")} className="text-cyan-300 hover:underline">walkthrough</Link>.
            </p>
            <div className="flex items-center gap-2 text-xs mt-2">
              <AlertTriangle className="w-3 h-3 text-amber-400 shrink-0" />
              <span className="text-amber-300/80">Trying to close without sign-off is refused by the system.</span>
            </div>
          </div>

          <div className="p-3 rounded-md bg-white/[0.02]" data-testid="text-rule-review">
            <div className="flex items-center gap-2 mb-2">
              <Eye className="w-4 h-4 text-purple-400" />
              <p className="text-sm text-white font-medium">Review Requirement</p>
            </div>
            <p className="text-xs text-white/55 leading-relaxed mb-2">
              When "Review Required" is switched on, the item <span className="text-white font-medium">cannot skip the review stages</span>. It can't jump straight from In Progress to With Secretariat to Send — it must pass through SDF MD review (and optionally EDGE MD review) first. The review path is set by the lane and locked at registration.
            </p>
            <div className="flex items-center gap-2 text-xs">
              <AlertTriangle className="w-3 h-3 text-amber-400 shrink-0" />
              <span className="text-amber-300/80">The shortcut from In Progress straight to Secretariat is blocked when review is required.</span>
            </div>
          </div>

          <div className="p-3 rounded-md bg-white/[0.02]" data-testid="text-rule-closing">
            <div className="flex items-center gap-2 mb-2">
              <FileText className="w-4 h-4 text-emerald-400" />
              <p className="text-sm text-white font-medium">Closing Note Requirement</p>
              <RoleBadge role="admin" />
            </div>
            <p className="text-xs text-white/55 leading-relaxed mb-2">
              Closing always needs a written note saying why the item is being closed (e.g. "Final sign-off received from Chairman on 15 Mar 2026"). The note is kept permanently in the history.
            </p>
            <div className="flex items-center gap-2 text-xs">
              <AlertTriangle className="w-3 h-3 text-amber-400 shrink-0" />
              <span className="text-amber-300/80">The Close Task button stays disabled until the note has at least a few characters.</span>
            </div>
          </div>

          <div className="p-3 rounded-md bg-white/[0.02]" data-testid="text-rule-dispatch">
            <div className="flex items-center gap-2 mb-2">
              <Send className="w-4 h-4 text-indigo-400" />
              <p className="text-sm text-white font-medium">Dispatch Note (Sent Upward)</p>
              <RoleBadge role="admin" />
            </div>
            <p className="text-xs text-white/55 leading-relaxed">
              Moving an item to <span className="text-white font-medium">Sent Upward</span> always needs a dispatch note recording where and how it was sent (e.g. "Sent to EDGE MD Office via email on 20 Mar 2026"). For above-MD lanes this happens via Mark Dispatched on the Awaiting Dispatch tab.
            </p>
          </div>
        </div>
      </Section>

      <Callout variant="tip" title="Where these rules are set">
        Lane review and sign-off requirements are set under <span className="text-white">Admin → Lanes</span> by a Super Admin. Per-item exceptions are chosen at registration time. Which fields are required lives under <span className="text-white">Admin → Field Permissions</span>.
      </Callout>
    </div>
  );
}
